import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useChatContext } from "stream-chat-react";
import { useScheduledMessages } from "./ScheduledMessagesContext";
import {
  COLLAPSED_SECTIONS_STORAGE_KEY,
  SIDEBAR_FILTERS,
  filterChannelsForSidebar,
  getChannelUnreadCount,
  getChannelUnreadMentionCount,
  groupChannelsIntoSections,
  parseCollapsedSections,
} from "./chatSidebarSectionUtils";

const readCollapsedSections = () => {
  if (typeof window === "undefined") return {};
  try {
    return parseCollapsedSections(
      window.localStorage.getItem(COLLAPSED_SECTIONS_STORAGE_KEY)
    );
  } catch (error) {
    return {};
  }
};

const writeCollapsedSections = (collapsed) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      COLLAPSED_SECTIONS_STORAGE_KEY,
      JSON.stringify(collapsed)
    );
  } catch (error) {
    // Storage may be unavailable (private mode); collapse state is a nicety
  }
};

// Unread totals on collapsed headers should track reads/new messages even
// though those events don't change the loadedChannels array identity
const UNREAD_EVENTS = [
  "message.new",
  "message.read",
  "notification.message_new",
  "notification.mark_read",
  "notification.mark_unread",
];

const useUnreadRevision = (client) => {
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!client) return undefined;
    const bump = () => setRevision((value) => value + 1);
    UNREAD_EVENTS.forEach((eventName) => client.on(eventName, bump));
    return () => {
      UNREAD_EVENTS.forEach((eventName) => client.off(eventName, bump));
    };
  }, [client]);
  return revision;
};

const FilterChips = ({ value, onChange, unreadCount, mentionCount }) => (
  <div className="rw-chat-filters" role="tablist" aria-label="Filter conversations">
    {SIDEBAR_FILTERS.map((filter) => {
      const count =
        filter.key === "unread"
          ? unreadCount
          : filter.key === "mentions"
            ? mentionCount
            : 0;
      const active = value === filter.key;
      return (
        <button
          key={filter.key}
          type="button"
          role="tab"
          aria-selected={active}
          className={`rw-chat-filter${active ? " rw-chat-filter--active" : ""}`}
          onClick={() => onChange(filter.key)}
        >
          {filter.label}
          {count > 0 && (
            <span className="rw-chat-filter__count">{count > 99 ? "99+" : count}</span>
          )}
        </button>
      );
    })}
  </div>
);

const FILTER_EMPTY_COPY = {
  unread: "You're all caught up.",
  mentions: "No unread mentions.",
};

const SectionHeader = React.forwardRef(function SectionHeader(
  { label, collapsed, unreadCount, onToggle },
  ref
) {
  return (
  <button
    ref={ref}
    type="button"
    className="rw-chat-section-header"
    onClick={onToggle}
    aria-expanded={!collapsed}
  >
    <span className="rw-chat-section-header__chevron">
      {collapsed ? (
        <ChevronRightIcon style={{ fontSize: "16px" }} />
      ) : (
        <ExpandMoreIcon style={{ fontSize: "16px" }} />
      )}
    </span>
    <span className="rw-chat-section-header__label">{label}</span>
    {collapsed && unreadCount > 0 && (
      <span className="rw-chat-section-header__unread">
        {unreadCount > 99 ? "99+" : unreadCount}
      </span>
    )}
  </button>
  );
});

// Slack-style "more unreads" pills: unread rows scrolled out of the sidebar
// viewport are counted above / below and the pill scrolls to the nearest one.
const findScrollParent = (element) => {
  let node = element?.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

// Unread rows count as offscreen when scrolled out of the viewport, or when
// their section is collapsed (positioned by the section header). Clicking
// the pill expands the section if needed and scrolls to the row.
const useOffscreenUnreads = (sections, collapsed, unreadRevision, expandSection) => {
  const rowRefs = useRef(new Map());
  const headerRefs = useRef(new Map());
  const containerRef = useRef(null);
  const [offscreen, setOffscreen] = useState({
    above: 0,
    below: 0,
    firstAbove: null,
    firstBelow: null,
  });
  // {cid, token}: the token makes every request a new object, so clicking the
  // same pill twice re-runs the effect instead of bailing out on equal state
  const [pendingScroll, setPendingScroll] = useState(null);
  const pendingTokenRef = useRef(0);
  const requestScroll = useCallback((cid) => {
    pendingTokenRef.current += 1;
    setPendingScroll({ cid, token: pendingTokenRef.current });
  }, []);

  const registerRow = useCallback((cid) => (element) => {
    if (element) rowRefs.current.set(cid, element);
    else rowRefs.current.delete(cid);
  }, []);

  const registerHeader = useCallback((key) => (element) => {
    if (element) headerRefs.current.set(key, element);
    else headerRefs.current.delete(key);
  }, []);

  // [{cid, sectionKey, collapsed}] for every unread row, in list order
  const unreadTargets = useMemo(() => {
    const targets = [];
    sections.forEach((section) => {
      section.channels.forEach((channel) => {
        if (getChannelUnreadCount(channel) > 0) {
          targets.push({
            cid: channel.cid,
            sectionKey: section.key,
            collapsed: Boolean(collapsed[section.key]),
          });
        }
      });
    });
    return targets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, collapsed, unreadRevision]);

  const recompute = useCallback(() => {
    const scrollParent = findScrollParent(containerRef.current);
    if (!scrollParent) {
      setOffscreen({ above: 0, below: 0, firstAbove: null, firstBelow: null });
      return;
    }
    const viewport = scrollParent.getBoundingClientRect();
    let above = 0;
    let below = 0;
    let firstAbove = null;
    let firstBelow = null;
    unreadTargets.forEach((target) => {
      const element = target.collapsed
        ? headerRefs.current.get(target.sectionKey)
        : rowRefs.current.get(target.cid);
      if (!element) return;
      const rect = element.getBoundingClientRect();
      // Collapsed sections count only while their header (with its unread
      // badge) is scrolled out of view; a visible badge already says it all
      if (rect.bottom <= viewport.top + 4) {
        above += 1;
        // Targets arrive in list order, so the last one above the viewport is
        // the nearest: that's the one the up pill should jump to
        firstAbove = target;
      } else if (rect.top >= viewport.bottom - 4) {
        below += 1;
        firstBelow = firstBelow ?? target;
      }
    });
    setOffscreen((previous) =>
      previous.above === above &&
      previous.below === below &&
      previous.firstAbove?.cid === firstAbove?.cid &&
      previous.firstBelow?.cid === firstBelow?.cid
        ? previous
        : { above, below, firstAbove, firstBelow }
    );
  }, [unreadTargets]);

  useLayoutEffect(() => {
    recompute();
    const scrollParent = findScrollParent(containerRef.current);
    if (!scrollParent) return undefined;
    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        recompute();
      });
    };
    scrollParent.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // Rows changing height (preview text wrapping, load-more mounting) moves
    // unread rows in and out of view without firing a scroll event
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(onScroll);
    observer?.observe(scrollParent);
    if (containerRef.current) observer?.observe(containerRef.current);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      scrollParent.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      observer?.disconnect();
    };
  }, [recompute]);

  const scrollToRow = useCallback((cid) => {
    const row = rowRefs.current.get(cid);
    if (!row) return false;
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
  }, []);

  // Once a collapsed section has been expanded and its rows are mounted,
  // finish the pill's scroll
  useLayoutEffect(() => {
    if (!pendingScroll) return;
    if (scrollToRow(pendingScroll.cid)) {
      setPendingScroll(null);
    }
  }, [pendingScroll, sections, collapsed, scrollToRow]);

  const jumpToTarget = useCallback(
    (target) => {
      if (!target) return;
      // Read the live collapse state rather than the flag captured on the
      // target: recompute keeps the previous target objects whenever the
      // counts are unchanged, so that flag can be stale
      const sectionCollapsed = Boolean(collapsed[target.sectionKey]);
      if (sectionCollapsed || !scrollToRow(target.cid)) {
        expandSection(target.sectionKey);
        requestScroll(target.cid);
      }
    },
    [collapsed, expandSection, requestScroll, scrollToRow]
  );

  return { containerRef, registerRow, registerHeader, offscreen, jumpToTarget };
};

const UnreadPill = ({ direction, count, onClick }) => (
  <button
    type="button"
    className={`rw-chat-unread-pill rw-chat-unread-pill--${direction}`}
    onClick={onClick}
  >
    {direction === "up" ? (
      <ArrowUpwardIcon style={{ fontSize: "14px" }} />
    ) : (
      <ArrowDownwardIcon style={{ fontSize: "14px" }} />
    )}
    <span>
      {count} unread {count === 1 ? "conversation" : "conversations"}
    </span>
  </button>
);

// Rendered through ChannelList's renderChannels hook: `channels` is Stream's
// loaded (and live-reordered) list, `renderChannel` produces the same
// ChannelPreview row the flat list would have shown.
function ChatSidebarSections({ channels, renderChannel }) {
  const { client } = useChatContext();
  const unreadRevision = useUnreadRevision(client);
  const [allCollapsed, setAllCollapsed] = useState(readCollapsedSections);
  const [filter, setFilter] = useState("all");
  // Filtered views (Unread / Mentions) start fully expanded and keep their
  // own, unpersisted collapse state; the "All" state is what's remembered
  const [filteredCollapsed, setFilteredCollapsed] = useState({});
  useEffect(() => {
    setFilteredCollapsed({});
  }, [filter]);
  const collapsed = filter === "all" ? allCollapsed : filteredCollapsed;

  const sections = useMemo(
    () => groupChannelsIntoSections(filterChannelsForSidebar(channels, filter)),
    // unreadRevision forces the per-section unread totals to recompute
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channels, filter, unreadRevision]
  );
  const unreadConversations = useMemo(
    () => channels.filter((channel) => getChannelUnreadCount(channel) > 0).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channels, unreadRevision]
  );
  const mentionConversations = useMemo(
    () => channels.filter((channel) => getChannelUnreadMentionCount(channel) > 0).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channels, unreadRevision]
  );

  const toggleSection = useCallback(
    (key) => {
      if (filter !== "all") {
        setFilteredCollapsed((previous) => ({ ...previous, [key]: !previous[key] }));
        return;
      }
      setAllCollapsed((previous) => {
        const next = { ...previous, [key]: !previous[key] };
        writeCollapsedSections(next);
        return next;
      });
    },
    [filter]
  );

  const expandSection = useCallback(
    (key) => {
      if (filter !== "all") {
        setFilteredCollapsed((previous) =>
          previous[key] ? { ...previous, [key]: false } : previous
        );
        return;
      }
      setAllCollapsed((previous) => {
        if (!previous[key]) return previous;
        const next = { ...previous, [key]: false };
        writeCollapsedSections(next);
        return next;
      });
    },
    [filter]
  );

  const { containerRef, registerRow, registerHeader, offscreen, jumpToTarget } =
    useOffscreenUnreads(sections, collapsed, unreadRevision, expandSection);

  return (
    <div className="rw-chat-sections" ref={containerRef}>
      <FilterChips
        value={filter}
        onChange={setFilter}
        unreadCount={unreadConversations}
        mentionCount={mentionConversations}
      />
      <ScheduledSidebarRow />
      {filter !== "all" && sections.length === 0 && (
        <p className="rw-chat-filter-empty">{FILTER_EMPTY_COPY[filter]}</p>
      )}
      {offscreen.above > 0 && (
        <div className="rw-chat-unread-pill-anchor rw-chat-unread-pill-anchor--top">
          <UnreadPill
            direction="up"
            count={offscreen.above}
            onClick={() => jumpToTarget(offscreen.firstAbove)}
          />
        </div>
      )}
      {sections.map((section) => (
        <div className="rw-chat-section" key={section.key}>
          <SectionHeader
            ref={registerHeader(section.key)}
            label={section.label}
            collapsed={Boolean(collapsed[section.key])}
            unreadCount={section.unreadCount}
            onToggle={() => toggleSection(section.key)}
          />
          {!collapsed[section.key] &&
            section.channels.map((channel) => (
              <div key={channel.cid} ref={registerRow(channel.cid)}>
                {renderChannel(channel)}
              </div>
            ))}
        </div>
      ))}
      {offscreen.below > 0 && (
        <div className="rw-chat-unread-pill-anchor rw-chat-unread-pill-anchor--bottom">
          <UnreadPill
            direction="down"
            count={offscreen.below}
            onClick={() => jumpToTarget(offscreen.firstBelow)}
          />
        </div>
      )}
    </div>
  );
}

// "Scheduled (N)": opens the list of messages waiting to send. Shown only
// while there is something scheduled (or a scheduled send that failed).
function ScheduledSidebarRow() {
  const scheduled = useScheduledMessages();
  const rows = scheduled?.scheduledMessages || [];
  if (!rows.length) return null;
  const failed = rows.some((row) => row.status === "failed");
  // Same full-width row and selected shade as a channel preview
  return (
    <button
      type="button"
      aria-current={scheduled.listOpen ? "page" : undefined}
      className={`flex w-full items-center gap-x-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 ${
        scheduled.listOpen ? "bg-slate-200 font-medium" : "bg-white"
      }`}
      onClick={() => scheduled.openList?.()}
    >
      <ScheduleIcon style={{ fontSize: 18 }} className="text-slate-500" />
      <span className="flex-1">Scheduled</span>
      <span
        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
          failed ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"
        }`}
      >
        {rows.length}
      </span>
    </button>
  );
}

export default ChatSidebarSections;
